import styled from "styled-components";
import dayjs from "dayjs";

import { styled as muiStyled } from "@mui/material/styles";
import { Box, Stack, Typography } from "@mui/material";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DownloadIcon from "@mui/icons-material/Download";
import PaymentIcon from "@mui/icons-material/Payment";

import { processPaymentOrder } from "src/api/tokenTransaction";

import font from "src/styles/font";
import { Black, DarkGrey, GreyStroke, White, White01 } from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency } from "src/common";
import { useLoading } from "src/context/LoadingContext";

import Button from "src/components/core/Button";
import Chip from "src/components/core/Chip";

const Dialog = muiStyled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
    position: "relative",
  },
  "& .MuiDialogContent-root": {
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 550,
    },
  },
  "& .MuiDialogActions-root": {
    // padding: 24,
    borderTop: `1px solid ${White01}`,
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 8px;
    right: 8px;
    color: ${Black};
  }
`;

const PaymentSummary = styled.div`
  width: 100%;
  padding: 16px;
  margin-bottom: 24px;
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WrapperOrderDetail = styled.div`
  width: 100%;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${White01};
`;

const TaxInvoiceInfo = styled.div`
  width: 100%;
  padding: 16px;
  background-color: ${White01};
  border: 1px solid ${GreyStroke};
  border-radius: 16px;
`;

const WaitingInfo = styled.div`
  width: 100%;
  margin-top: 24px;
  padding: 16px;
  background-color: #fff7ed;
  border: 1px solid #ffedd4;
  border-radius: 16px;
  display: flex;
  align-items: center;
`;

const OrderDetailDialog = ({ onClose, orderDetail }) => {
  const { setLoading } = useLoading();

  const processPayment = async () => {
    try {
      setLoading(true);

      const processPayment = await processPaymentOrder(orderDetail._id);

      if (!!processPayment) {
        window.open(processPayment?.payment_url, "_blank");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle sx={{ p: 3 }}>
        รายละเอียดการชำระเงิน
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" sx={{ color: DarkGrey }} />
        </CloseButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          width: "100%",
          "::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <PaymentSummary>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ m: 0 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                backgroundColor: White,
                borderRadius: "50%",
                p: 1.5,
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)",
              }}
            >
              <CreditCardIcon sx={{ color: "#155DFC" }} />
            </Box>

            <Box>
              <Typography variant="subtitle1" color="#155DFC">
                ยอดชำระสุทธิ
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {`${convertIntToCurrency(orderDetail?.price, false)} บาท`}
              </Typography>
            </Box>
          </Box>

          <Chip
            icon={
              orderDetail?.status === "PAID" ? (
                <CheckCircleOutlinedIcon />
              ) : orderDetail?.status === "PENDING" ? (
                <AccessTimeIcon />
              ) : (
                <HighlightOffIcon />
              )
            }
            label={
              orderDetail?.status === "PAID"
                ? "ชำระแล้ว"
                : orderDetail?.status === "PENDING"
                  ? "รอชำระ"
                  : orderDetail?.status === "CANCELLED"
                    ? "ยกเลิก"
                    : orderDetail?.status === "EXPIRED"
                      ? "หมดอายุ"
                      : "ไม่สำเร็จ"
            }
            variant="secondary"
            color={
              orderDetail?.status === "PAID"
                ? "success"
                : orderDetail?.status === "PENDING"
                  ? "warning"
                  : "error"
            }
          />
        </PaymentSummary>

        <Stack spacing={2} sx={{ mb: 3 }}>
          <WrapperOrderDetail>
            <Typography variant="body2">วันที่ทำรายการ</Typography>

            <Typography variant="body1">
              {dayjs(orderDetail?.createdAt).format("DD/MM/YYYY HH:mm")}
            </Typography>
          </WrapperOrderDetail>

          <WrapperOrderDetail>
            <Typography variant="body2">เลขที่ใบเสร็จ</Typography>

            <Typography variant="body1">{orderDetail?.order_no}</Typography>
          </WrapperOrderDetail>

          <WrapperOrderDetail>
            <Typography variant="body2">แพ็กเกจ</Typography>

            <Typography variant="body1">
              {orderDetail?.package?.name}
            </Typography>
          </WrapperOrderDetail>

          <WrapperOrderDetail>
            <Typography variant="body2">ช่องทางชำระเงิน</Typography>

            <Typography variant="body1">
              {orderDetail?.payment_method === "credit_card" ? (
                <>
                  Credit Card
                  {!!orderDetail?.payment_provider && (
                    <Typography
                      variant="caption"
                      color={DarkGrey}
                      sx={{ ml: 1 }}
                    >
                      {/* (**** **** {4444}) */}
                      {`(${orderDetail?.payment_provider})`}
                    </Typography>
                  )}
                </>
              ) : orderDetail?.payment_method === "promptpay" ? (
                `PromptPay`
              ) : orderDetail?.payment_method === "truewallet" ? (
                `TrueWallet`
              ) : (
                `Other`
              )}
            </Typography>
          </WrapperOrderDetail>
        </Stack>

        {orderDetail?.billing?.is_company ? (
          <TaxInvoiceInfo>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              marginBottom={1.5}
            >
              <ApartmentIcon sx={{ color: DarkGrey }} />
              <Typography variant="subtitle2">ข้อมูลใบกำกับภาษี</Typography>
            </Stack>

            <Typography variant="subtitle1" marginBottom={0.5}>
              {orderDetail?.billing?.name || "-"}
            </Typography>

            <Typography
              variant="body2"
              marginBottom={0.5}
              fontSize={font.size.sm}
            >
              {`เลขประจำตัวผู้เสียภาษี: ${orderDetail?.billing?.tax_id || "-"}`}
            </Typography>

            <Typography variant="body2" fontSize={font.size.sm}>
              {orderDetail?.billing?.address || "-"}
            </Typography>
          </TaxInvoiceInfo>
        ) : (
          <TaxInvoiceInfo>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              marginBottom={1.5}
            >
              <ApartmentIcon sx={{ color: DarkGrey }} />
              <Typography variant="subtitle2">ที่อยู่ใบเสร็จ</Typography>
            </Stack>

            <Typography variant="subtitle1" marginBottom={0.5}>
              {orderDetail?.billing?.name || "-"}
            </Typography>

            <Typography variant="body2" fontSize={font.size.sm}>
              {orderDetail?.billing?.address || "-"}
            </Typography>
          </TaxInvoiceInfo>
        )}

        {orderDetail?.status === "PAID" &&
          !checkNotEmpty(orderDetail?.receipt_file_url) && (
            <>
              <WaitingInfo>
                <AccessTimeIcon sx={{ color: "#CA3500" }} />
                <Typography
                  variant="body2"
                  color="#CA3500"
                  fontSize={font.size.sm}
                  marginLeft={1}
                >
                  ใบเสร็จรับเงินอยู่ระหว่างการดำเนินการ (ใช้เวลา 2-5 วันทำการ)
                  คุณสามารถดาวน์โหลดได้ในภายหลัง
                </Typography>
              </WaitingInfo>

              <Box mt={3}>
                <img
                  src="/assets/banner-seller-topup.png"
                  alt="ad"
                  width="100%"
                  height="100%"
                />
              </Box>
            </>
          )}
      </DialogContent>

      {orderDetail?.status === "PENDING" && (
        <DialogActions
          sx={{
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            fullWidth
            startIcon={<PaymentIcon />}
            onClick={() => processPayment()}
            sx={{ maxWidth: 180 }}
          >
            ชำระเงินอีกครั้ง
          </Button>
        </DialogActions>
      )}

      {checkNotEmpty(orderDetail?.receipt_file_url) && (
        <DialogActions sx={{ p: 3 }}>
          <Button
            variant="dark"
            fullWidth
            startIcon={<DownloadIcon />}
            onClick={() => window.open(orderDetail?.receipt_file_url, "_blank")}
            sx={{ maxWidth: 180 }}
          >
            ดาวน์โหลดใบเสร็จ
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default OrderDetailDialog;
